import React, { useState, useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import {
  LightBulbIcon,
  PaperAirplaneIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  SparklesIcon,
  ClockIcon,
  BoltIcon,
  ChartBarIcon,
  CpuChipIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  RocketLaunchIcon,
  PlusIcon,
  ArrowLeftIcon,
  FolderOpenIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const API_URL = (() => {
  const r = import.meta.env.VITE_API_URL || 'http://localhost:3334'
  return r.endsWith('/api') ? r.slice(0, -4) : r
})()

// ── Types ────────────────────────────────────────────────────────────────────
interface UseCase {
  problem: string
  solution: string
  tools: string[]
  impact: string
  complexity: string
  quick_win: boolean
}

interface StrategySection {
  title: string
  use_cases: UseCase[]
}

interface Report {
  business_summary: string
  sales_improvement: StrategySection
  cost_reduction: StrategySection
  operational_efficiency: StrategySection
  priority_roadmap: {
    week_1_2: string[]
    week_3_4: string[]
    month_2_plus: string[]
  }
  cto_note: string
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface FormData {
  name: string
  industry: string
  businessModel: string
  companySize: string
  revenueStage: string
  currentTools: string
  painPoints: string[]
  geography: string
  additionalNotes: string
}

interface SessionSummary {
  id: string
  name: string | null
  status: string
  confidenceScore: number
  industry: string
  businessModel: string
  createdAt: string
}

// ── Constants ────────────────────────────────────────────────────────────────
const INDUSTRIES = [
  'Healthcare', 'FinTech', 'EdTech', 'E-commerce', 'Real Estate',
  'Manufacturing', 'Logistics', 'SaaS', 'Legal', 'Media & Entertainment',
  'Retail', 'Insurance', 'Travel & Hospitality', 'Agriculture', 'Energy',
  'Consulting', 'Recruitment / HR Tech', 'Automotive', 'Other',
]

const BUSINESS_MODELS = ['B2B', 'B2C', 'Marketplace', 'SaaS', 'D2C', 'Other']

const COMPANY_SIZES = ['1-10', '10-50', '50-200', '200+']

const REVENUE_STAGES = ['Pre-revenue', 'Early', 'Growth', 'Scale']

const PAIN_POINTS = [
  'Lead generation',
  'Low conversion',
  'Ops inefficiency',
  'High costs',
  'Manual work',
  'Poor customer retention',
  'Slow onboarding',
  'Data silos',
]

// ── Step Indicator ───────────────────────────────────────────────────────────
const StepIndicator: React.FC<{ current: number }> = ({ current }) => {
  const steps = ['Business Profile', 'Strategy Synthesis', 'Strategic Roadmap']
  return (
    <div className="flex items-center justify-center mb-12 relative">
      <div className="absolute top-1/2 left-0 w-full h-px bg-white/5 -z-10"></div>
      <div className="flex items-center gap-12 sm:gap-24 relative z-10">
        {steps.map((s, i) => (
          <div key={i} className="flex flex-col items-center gap-3">
            <div className={`
              w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs transition-all duration-500
              ${current >= i + 1 
                ? 'bg-primary text-white shadow-[0_0_20px_rgba(255,107,0,0.4)] ring-4 ring-primary/20 scale-110' 
                : 'bg-[#131B2C] text-text-secondary border border-white/10'
              }
            `}>
              {current > i + 1 ? <CheckCircleIcon className="w-6 h-6" /> : `0${i + 1}`}
            </div>
            <span className={`text-[10px] font-black uppercase tracking-[0.2em] whitespace-nowrap ${current >= i + 1 ? 'text-primary' : 'text-text-secondary/40'}`}>
              {s}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Project Dashboard (Step 0) ───────────────────────────────────────────────
const ProjectDashboard: React.FC<{
  sessions: SessionSummary[]
  isLoading: boolean
  onNewProject: () => void
  onResumeSession: (sessionId: string) => void
}> = ({ sessions, isLoading, onNewProject, onResumeSession }) => {
  const statusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg bg-primary/10 text-primary border border-primary/20 flex items-center gap-1 shadow-[0_0_10px_rgba(255,107,0,0.1)]">
            <BoltIcon className="w-3 h-3" /> ACTIVE SESSION
          </span>
        )
      case 'completed':
        return (
          <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg bg-green-500/10 text-green-500 border border-green-500/20 flex items-center gap-1 shadow-[0_0_10px_rgba(16,185,129,0.1)]">
            <CheckCircleIcon className="w-3 h-3" /> COMPLETED
          </span>
        )
      default:
        return (
          <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg bg-white/5 text-text-secondary border border-white/10 uppercase">
            {status}
          </span>
        )
    }
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  return (
    <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-10">
        <div>
          <h2 className="text-xl font-black text-white uppercase tracking-tight font-heading">
            AI Strategy Projects
          </h2>
          <p className="text-xs text-text-secondary font-medium mt-1">
            {sessions.length === 0
              ? 'Create your first AI strategy project'
              : `${sessions.length} strategy session${sessions.length > 1 ? 's' : ''} established`}
          </p>
        </div>
        <button
          onClick={onNewProject}
          className="btn-primary py-3 px-6 text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-3 shadow-[0_0_20px_rgba(255,107,0,0.3)] hover:scale-[1.05] transition-all"
          id="ai-strategy-new-project"
        >
          <PlusIcon className="w-4 h-4" />
          NEW PROJECT
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-24 bg-[#131B2C]/30 border border-white/5 rounded-3xl backdrop-blur-sm">
          <div className="relative">
            <ArrowPathIcon className="w-12 h-12 text-primary animate-spin opacity-20" />
            <CpuChipIcon className="w-6 h-6 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
          </div>
          <span className="mt-6 text-[10px] font-black uppercase tracking-[0.3em] text-text-secondary animate-pulse">Loading Projects...</span>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && sessions.length === 0 && (
        <div className="bg-[#131B2C]/40 backdrop-blur-xl rounded-3xl border border-white/10 shadow-3xl p-16 text-center group overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-[100px] -z-10 group-hover:bg-primary/10 transition-all"></div>
          <div className="w-20 h-20 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-6 shadow-inner group-hover:scale-110 transition-transform duration-500">
            <FolderOpenIcon className="w-10 h-10 text-white/20" />
          </div>
          <h3 className="text-xl font-black text-white mb-3 uppercase tracking-tight font-heading">No Projects Found</h3>
          <p className="text-sm text-text-secondary max-w-sm mx-auto mb-10 font-medium">
            You haven't created any AI strategy projects yet. Start a new project to generate your customized AI strategy report.
          </p>
          <button
            onClick={onNewProject}
            className="px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-[10px] font-black uppercase tracking-[0.3em] text-white transition-all hover:border-primary/40 shadow-inner inline-flex items-center gap-3"
          >
            <RocketLaunchIcon className="w-5 h-5 text-primary" />
            CREATE NEW PROJECT
          </button>
        </div>
      )}

      {/* Session Cards */}
      {!isLoading && sessions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {sessions.map((session, i) => (
            <button
              key={session.id}
              onClick={() => onResumeSession(session.id)}
              className="bg-[#131B2C]/40 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl p-6 text-left hover:shadow-primary/5 hover:border-primary/40 hover:-translate-y-1.5 transition-all duration-500 animate-in fade-in slide-in-from-bottom-2 duration-500 group relative overflow-hidden"
              style={{ animationDelay: `${i * 80}ms` }}
              id={`ai-strategy-session-${session.id}`}
            >
              <div className="absolute top-0 right-0 p-4">
                {statusBadge(session.status)}
              </div>

              <div className="mb-8">
                <h3 className="text-lg font-black text-white group-hover:text-primary transition-colors line-clamp-1 uppercase tracking-tight font-heading mb-1">
                  {session.name || `${session.industry} Strategy`}
                </h3>
                <div className="flex items-center gap-3 text-[9px] font-black uppercase tracking-widest text-text-secondary/40">
                   <ClockIcon className="w-3.5 h-3.5" />
                   MODIFIED: {formatDate(session.createdAt)}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-[#0B0F19]/60 rounded-xl p-3 border border-white/5">
                  <span className="block text-[8px] font-black uppercase text-text-secondary/30 mb-1 tracking-widest">Industry</span>
                  <span className="text-[10px] font-bold text-text-secondary truncate block">{session.industry}</span>
                </div>
                <div className="bg-[#0B0F19]/60 rounded-xl p-3 border border-white/5">
                  <span className="block text-[8px] font-black uppercase text-text-secondary/30 mb-1 tracking-widest">Model</span>
                  <span className="text-[10px] font-bold text-text-secondary truncate block">{session.businessModel}</span>
                </div>
              </div>

              {/* Confidence Bar */}
              <div className="space-y-2">
                <div className="flex w-full justify-between items-center text-[9px] font-black uppercase tracking-widest">
                  <span className="text-text-secondary/40">AI Confidence</span>
                  <span className={session.status === 'completed' ? 'text-green-500' : 'text-primary'}>
                    {session.status === 'completed' ? 'COMPLETED' : `${session.confidenceScore}%`}
                  </span>
                </div>
                <div className="w-full bg-[#0B0F19] rounded-full h-1.5 overflow-hidden border border-white/5 shadow-inner">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ${
                      session.status === 'completed'
                        ? 'bg-green-500 shadow-[0_0_10px_rgba(16,185,129,0.3)]'
                        : 'bg-gradient-to-r from-primary to-orange-400 shadow-[0_0_10px_rgba(255,107,0,0.3)]'
                    }`}
                    style={{ width: `${session.status === 'completed' ? 100 : session.confidenceScore}%` }}
                  />
                </div>
              </div>

              {/* Action hint */}
              <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[10px] font-black text-primary uppercase tracking-[0.2em] transition-all">
                  {session.status === 'completed' ? 'VIEW REPORT' : 'RESUME CHAT'}
                  <ChevronRightIcon className="w-3.5 h-3.5" />
                </div>
                <div className="p-2 bg-primary/20 rounded-lg border border-primary/30 group-hover:bg-primary transition-all">
                  <RocketLaunchIcon className="w-4 h-4 text-primary group-hover:text-white" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Smart Form (Step 1) ──────────────────────────────────────────────────────
const StrategyForm: React.FC<{
  onSubmit: (data: FormData) => void
  onBack: () => void
  isLoading: boolean
}> = ({ onSubmit, onBack, isLoading }) => {
  const [form, setForm] = useState<FormData>({
    name: '',
    industry: '',
    businessModel: '',
    companySize: '',
    revenueStage: '',
    currentTools: '',
    painPoints: [],
    geography: '',
    additionalNotes: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.industry || !form.businessModel || !form.companySize || !form.revenueStage) {
      toast.error('Please fill in all required fields')
      return
    }
    onSubmit(form)
  }

  const togglePain = (p: string) => {
    setForm((prev) => ({
      ...prev,
      painPoints: prev.painPoints.includes(p)
        ? prev.painPoints.filter((x) => x !== p)
        : [...prev.painPoints, p],
    }))
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="bg-[#131B2C]/40 backdrop-blur-xl rounded-3xl border border-white/10 shadow-3xl p-8 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-[100px] -z-10 group-hover:bg-primary/10 transition-all"></div>
        
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center shadow-[0_0_15px_rgba(255,107,0,0.2)]">
              <SparklesIcon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-black text-white uppercase tracking-tight font-heading">
                Business Profile
              </h2>
              <p className="text-xs text-text-secondary font-medium">Tell us about your business to get a tailored AI strategy.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onBack}
            className="text-[10px] font-black text-text-secondary/40 hover:text-primary flex items-center gap-2 transition-all uppercase tracking-widest"
          >
            <ArrowLeftIcon className="w-4 h-4" /> CANCEL
          </button>
        </div>

        <div className="space-y-8">
          {/* Project Name */}
          <div className="group/field">
            <label className="block text-[10px] font-black text-white uppercase tracking-[0.2em] mb-2 px-1">
              Project Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-[#0B0F19]/60 border border-white/10 rounded-xl p-4 text-white text-sm font-medium focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all placeholder:text-text-secondary/20"
              placeholder="e.g. Acme Corp AI Strategy"
              id="ai-strategy-name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Industry */}
            <div className="group/field">
              <label className="block text-[10px] font-black text-white uppercase tracking-[0.2em] mb-2 px-1">
                Industry <span className="text-primary">*</span>
              </label>
              <select
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className="w-full bg-[#0B0F19]/60 border border-white/10 rounded-xl p-4 text-white text-sm font-medium focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all appearance-none cursor-pointer"
                id="ai-strategy-industry"
              >
                <option value="" className="bg-[#0B0F19]">Select Industry</option>
                {INDUSTRIES.map((i) => (
                  <option key={i} value={i} className="bg-[#0B0F19]">{i}</option>
                ))}
              </select>
            </div>

            {/* Geography */}
            <div className="group/field">
              <label className="block text-[10px] font-black text-white uppercase tracking-[0.2em] mb-2 px-1">
                Geography
              </label>
              <input
                type="text"
                value={form.geography}
                onChange={(e) => setForm({ ...form, geography: e.target.value })}
                className="w-full bg-[#0B0F19]/60 border border-white/10 rounded-xl p-4 text-white text-sm font-medium focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all placeholder:text-text-secondary/20"
                placeholder="e.g. G20, EMEA, Global"
                id="ai-strategy-geography"
              />
            </div>
          </div>

          {/* Business Model */}
          <div>
            <label className="block text-[10px] font-black text-white uppercase tracking-[0.2em] mb-3 px-1">
              Business Model <span className="text-primary">*</span>
            </label>
            <div className="flex flex-wrap gap-3">
              {BUSINESS_MODELS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setForm({ ...form, businessModel: m })}
                  className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all duration-300 ${
                    form.businessModel === m
                      ? 'bg-primary text-white border-primary shadow-[0_0_15px_rgba(255,107,0,0.3)] scale-[1.05]'
                      : 'bg-white/5 text-text-secondary border-white/10 hover:border-primary/40 hover:bg-white/10'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Company Size & Revenue */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="block text-[10px] font-black text-white uppercase tracking-[0.2em] mb-3 px-1">
                Company Size <span className="text-primary">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {COMPANY_SIZES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm({ ...form, companySize: s })}
                    className={`px-3 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all duration-300 ${
                      form.companySize === s
                        ? 'bg-primary text-white border-primary shadow-[0_0_10px_rgba(255,107,0,0.2)]'
                        : 'bg-white/5 text-text-secondary border-white/10 hover:border-primary/30'
                    }`}
                  >
                    {s} units
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black text-white uppercase tracking-[0.2em] mb-3 px-1">
                Revenue Stage <span className="text-primary">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {REVENUE_STAGES.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setForm({ ...form, revenueStage: r })}
                    className={`px-3 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all duration-300 ${
                      form.revenueStage === r
                        ? 'bg-primary text-white border-primary shadow-[0_0_10px_rgba(255,107,0,0.2)]'
                        : 'bg-white/5 text-text-secondary border-white/10 hover:border-primary/30'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Current Tools */}
          <div className="group/field">
            <label className="block text-[10px] font-black text-white uppercase tracking-[0.2em] mb-2 px-1">Current Tools / Software</label>
            <input
              type="text"
              value={form.currentTools}
              onChange={(e) => setForm({ ...form, currentTools: e.target.value })}
              className="w-full bg-[#0B0F19]/60 border border-white/10 rounded-xl p-4 text-white text-sm font-medium focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all placeholder:text-text-secondary/20"
              placeholder="e.g. AWS, Snowflake, Salesforce, Slack..."
              id="ai-strategy-tools"
            />
          </div>

          {/* Pain Points */}
          <div>
            <label className="block text-[10px] font-black text-white uppercase tracking-[0.2em] mb-3 px-1">Key Pain Points</label>
            <div className="flex flex-wrap gap-2">
              {PAIN_POINTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => togglePain(p)}
                  className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all duration-300 ${
                    form.painPoints.includes(p)
                      ? 'bg-error text-white border-error shadow-[0_0_10px_rgba(255,59,48,0.2)]'
                      : 'bg-white/5 text-text-secondary border-white/10 hover:border-error/40 hover:bg-error/5'
                  }`}
                >
                  {form.painPoints.includes(p) ? '✓ ' : ''}{p}
                </button>
              ))}
            </div>
          </div>

          {/* Additional Notes */}
          <div className="group/field">
            <label className="block text-[10px] font-black text-white uppercase tracking-[0.2em] mb-2 px-1">Additional Notes (Optional)</label>
            <textarea
              value={form.additionalNotes}
              onChange={(e) => setForm({ ...form, additionalNotes: e.target.value })}
              className="w-full bg-[#0B0F19]/60 border border-white/10 rounded-xl p-4 text-white text-sm font-medium focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all placeholder:text-text-secondary/20 min-h-[100px] resize-none"
              placeholder="Any specific challenges or goals you want the AI to focus on..."
              id="ai-strategy-notes"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full mt-10 h-16 bg-gradient-to-r from-primary to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-2xl shadow-[0_0_30px_rgba(255,107,0,0.3)] font-black uppercase tracking-[0.3em] text-xs disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-4 transition-all hover:scale-[1.01] active:scale-[0.99]"
          id="ai-strategy-submit"
        >
          {isLoading ? (
            <>
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
              ANALYZING...
            </>
          ) : (
            <>
              <RocketLaunchIcon className="w-5 h-5" />
              START AI DISCOVERY
              <ChevronRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>
      </div>
    </form>
  )
}

// ── Guided Chat (Step 2) ─────────────────────────────────────────────────────
const StrategyChat: React.FC<{
  sessionId: string
  initialMessages: ChatMessage[]
  initialConfidence: number
  onComplete: (report: Report) => void
  onBack: () => void
}> = ({ sessionId, initialMessages, initialConfidence, onComplete, onBack }) => {
  const { token } = useSelector((state: RootState) => state.auth)
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages)
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [confidence, setConfidence] = useState(initialConfidence)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    inputRef.current?.focus()
  }, [isLoading])

  const sendMessage = async (forceGenerate: boolean = false, isRetry: boolean = false) => {
    if ((!input.trim() && !forceGenerate && !isRetry) || isLoading) return

    setHasError(false)
    let messageToSend = ''

    if (isRetry) {
      if (messages.length === 0) return
      const lastMsg = messages[messages.length - 1]
      messageToSend = lastMsg.role === 'user' && !forceGenerate ? lastMsg.content : ''
    } else {
      const userMsg = input.trim()
      if (!forceGenerate) {
        setMessages((prev) => [...prev, { role: 'user', content: userMsg }])
        setInput('')
        messageToSend = userMsg
      } else {
        setMessages((prev) => [...prev, { role: 'user', content: "Generate the best strategy report you can with the current context." }])
        messageToSend = ''
      }
    }

    setIsLoading(true)

    try {
      const res = await fetch(`${API_URL}/api/ai-strategy/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ sessionId, message: messageToSend, forceGenerate }),
      })

      const json = await res.json()

      if (!json.success) throw new Error(json.message)

      const { reply, isComplete, report, confidenceScore } = json.data

      if (confidenceScore) {
        setConfidence(confidenceScore)
      }

      if (reply) {
        setMessages((prev) => [...prev, { role: 'assistant', content: reply }])
      }

      if (isComplete && report) {
        setTimeout(() => onComplete(report), 1500)
      }
    } catch (err: any) {
      setHasError(true)
      toast.error(err.message || 'Failed to send message')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="bg-[#131B2C]/40 backdrop-blur-xl rounded-3xl border border-white/10 shadow-3xl overflow-hidden flex flex-col" style={{ height: '70vh', minHeight: '600px' }}>
        {/* Chat Header */}
        <div className="px-6 py-5 border-b border-white/5 bg-white/[0.02] flex justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-text-secondary hover:text-white hover:bg-white/10 transition-all shadow-inner"
              title="Back to Dashboard"
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>
            <div className="w-11 h-11 flex-shrink-0 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center shadow-[0_0_15px_rgba(255,107,0,0.2)]">
              <CpuChipIcon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-black text-white uppercase tracking-widest font-heading">
                AI Business Architect
              </h3>
              <p className="text-[9px] font-black uppercase tracking-[0.2em] text-primary mt-0.5">
                {isLoading ? 'Analyzing...' : 'Ready — answer to unlock your strategy'}
              </p>
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-2 w-1/3 min-w-[160px]">
            <div className="flex w-full justify-between items-center text-[9px] font-black uppercase tracking-[0.2em]">
              <span className="text-text-secondary">AI Confidence</span>
              <span className="text-primary font-mono">{confidence}%</span>
            </div>
            <div className="w-full bg-[#0B0F19] rounded-full h-1.5 overflow-hidden border border-white/5">
              <div 
                className="bg-gradient-to-r from-primary to-orange-400 h-full rounded-full transition-all duration-1000 shadow-[0_0_10px_rgba(255,107,0,0.4)]" 
                style={{ width: `${confidence}%` }}
              />
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-[#0B0F19]/40">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-400`}
            >
              <div
                className={`max-w-[85%] px-5 py-4 rounded-2xl text-sm font-medium leading-relaxed tracking-tight ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-br from-primary to-orange-600 text-white rounded-tr-none shadow-[0_10px_30px_rgba(255,107,0,0.15)] ml-12'
                    : 'bg-[#131B2C] text-text-secondary border border-white/10 rounded-tl-none mr-12'
                }`}
              >
                <div className="whitespace-pre-wrap">{msg.content}</div>
                {msg.role === 'assistant' && (
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-pulse" />
                    <span className="text-[8px] font-black uppercase tracking-[0.3em] text-text-secondary opacity-40">AI Generated</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          {hasError && !isLoading && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
            <div className="flex justify-start animate-in fade-in duration-300">
              <div className="bg-error/10 border border-error/20 rounded-2xl p-4 flex flex-col gap-3 max-w-[85%]">
                <p className="text-xs font-bold text-error">Message processing failed.</p>
                <button
                  onClick={() => {
                    const lastMsg = messages[messages.length - 1]
                    const forceGen = lastMsg.content === "Generate the best strategy report you can with the current context."
                    sendMessage(forceGen, true)
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-error hover:bg-red-600 text-white text-xs font-black uppercase tracking-widest px-4 py-2.5 rounded-xl transition-all shadow-[0_0_15px_rgba(255,59,48,0.2)]"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  Retry Message
                </button>
              </div>
            </div>
          )}

          {isLoading && (
            <div className="flex justify-start animate-in fade-in duration-300">
              <div className="bg-[#131B2C] border border-white/10 rounded-2xl rounded-tl-none px-6 py-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce shadow-[0_0_8px_rgba(255,107,0,0.5)]" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce shadow-[0_0_8px_rgba(255,107,0,0.5)]" style={{ animationDelay: '200ms' }} />
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce shadow-[0_0_8px_rgba(255,107,0,0.5)]" style={{ animationDelay: '400ms' }} />
                </div>
              </div>
            </div>
          )}

          {!isLoading && confidence >= 70 && (
            <div className="flex justify-center animate-in zoom-in duration-500 pt-4">
              <button
                onClick={() => sendMessage(true)}
                className="group flex items-center gap-3 px-6 py-3 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] text-primary transition-all shadow-[0_0_20px_rgba(255,107,0,0.1)] hover:scale-[1.02]"
              >
                <SparklesIcon className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                Generate Report (70% Confidence)
              </button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-6 py-4 border-t border-white/5 bg-[#131B2C]/60 backdrop-blur-3xl">
          <div className="flex items-center gap-3 relative">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage(false)}
              placeholder="Type your answer here..."
              disabled={isLoading}
              className="flex-1 bg-[#0B0F19] border border-white/10 rounded-xl px-5 py-4 text-sm font-medium text-white focus:outline-none focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all placeholder:text-text-secondary/20 disabled:opacity-30"
              id="ai-strategy-chat-input"
            />
            <button
              onClick={() => sendMessage(false)}
              disabled={isLoading || !input.trim()}
              className="h-14 w-14 rounded-xl bg-gradient-to-r from-primary to-orange-600 text-white flex items-center justify-center shadow-[0_0_20px_rgba(255,107,0,0.3)] hover:scale-[1.05] transition-all disabled:opacity-20 disabled:cursor-not-allowed group"
              id="ai-strategy-chat-send"
            >
              <PaperAirplaneIcon className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Use Case Card ────────────────────────────────────────────────────────────
const UseCaseCard: React.FC<{ useCase: UseCase; index: number }> = ({ useCase, index }) => (
  <div className="use-case-card bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-xl hover:border-orange-400 transition-all duration-500 hover:-translate-y-1 animate-in fade-in slide-in-from-bottom-2 duration-500 relative overflow-hidden group" style={{ animationDelay: `${index * 100}ms` }}>
    <div className="absolute top-0 right-0 p-3">
      {useCase.quick_win && (
        <span className="text-[8px] font-black bg-orange-100 text-orange-600 px-2.5 py-1 rounded-lg border border-orange-200 flex items-center gap-1 uppercase tracking-widest">
          <BoltIcon className="w-3 h-3" /> Quick Win
        </span>
      )}
    </div>

    <div className="flex items-center gap-2 mb-4">
      <span className={`text-[9px] font-black uppercase tracking-[0.2em] px-2.5 py-1 rounded-md border ${
        useCase.complexity === 'Low' ? 'bg-green-100 text-green-700 border-green-200' :
        useCase.complexity === 'Medium' ? 'bg-orange-100 text-orange-600 border-orange-200' :
        'bg-red-100 text-red-600 border-red-200'
      }`}>
        {useCase.complexity} Velocity
      </span>
    </div>

    <h4 className="text-base font-black text-gray-900 mb-3 uppercase tracking-tight group-hover:text-orange-600 transition-colors">{useCase.problem}</h4>
    <p className="text-sm text-gray-600 leading-relaxed mb-6 font-medium italic border-l-2 border-gray-200 pl-4">{useCase.solution}</p>

    <div className="flex flex-wrap gap-2 mb-6">
      {useCase.tools.map((tool) => (
        <span key={tool} className="text-[9px] font-bold bg-gray-50 text-gray-500 px-2.5 py-1 rounded-lg border border-gray-200 uppercase tracking-widest">
          {tool}
        </span>
      ))}
    </div>

    <div className="flex items-center gap-2 text-[10px] text-orange-600 font-black uppercase tracking-[0.2em]">
      <ChartBarIcon className="w-4 h-4" />
      Impact: {useCase.impact}
    </div>
  </div>
)

// ── Strategy Report (Step 3) ─────────────────────────────────────────────────
const StrategyReport: React.FC<{ report: Report; onBack: () => void }> = ({ report, onBack }) => {
  const reportRef = useRef<HTMLDivElement>(null)

  const sections = [
    { key: 'sales_improvement', data: report.sales_improvement, icon: '📈', color: 'blue' },
    { key: 'cost_reduction', data: report.cost_reduction, icon: '💰', color: 'emerald' },
    { key: 'operational_efficiency', data: report.operational_efficiency, icon: '⚙️', color: 'purple' },
  ]

  const exportAsJson = () => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ai-strategy-report-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success('Report exported as JSON')
  }

  const exportAsCsv = () => {
    const rows = [['Section', 'Problem', 'Solution', 'Tools', 'Impact', 'Complexity', 'Quick Win']]
    sections.forEach((s) => {
      s.data.use_cases.forEach((uc) => {
        rows.push([
          s.data.title,
          uc.problem,
          uc.solution,
          uc.tools.join(', '),
          uc.impact,
          uc.complexity,
          uc.quick_win ? 'Yes' : 'No',
        ])
      })
    })

    const csvContent = rows.map((e) => e.map(item => `"${item.toString().replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ai-strategy-usecases-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success('Use cases exported as CSV')
  }

  const copyToClipboard = () => {
    const text = `
AI STRATEGY REPORT

SUMMARY:
${report.business_summary}

CTO NOTE:
${report.cto_note}

Key Use Cases:
${sections.map(s => `\n--- ${s.data.title} ---\n${s.data.use_cases.map(uc => `- ${uc.problem}: ${uc.solution} (${uc.impact})`).join('\n')}`).join('\n')}
    `.trim()

    navigator.clipboard.writeText(text).then(() => {
      toast.success('Report summary copied to clipboard')
    })
  }

  const exportAsPdf = () => {
    const printStyle = document.createElement('style')
    printStyle.id = 'strategy-print-styles'
    printStyle.textContent = `
      @media print {
        body, html { 
          background: white !important; 
        }
        body * { visibility: hidden !important; }
        #strategy-report-printable, #strategy-report-printable * { 
          visibility: visible !important; 
        }
        #root, main, div {
          overflow: visible !important;
          height: auto !important;
          max-height: none !important;
        }

        #strategy-report-printable {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 24px !important;
        }

        .strategy-section, .use-case-card, .roadmap-item {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
          margin-bottom: 24px !important;
          display: block !important;
        }
        
        .roadmap-grid {
          display: flex !important;
          flex-direction: column !important;
          gap: 16px !important;
        }

        .no-print { display: none !important; }
        
        @page { 
          margin: 15mm;
          size: auto;
        }
      }
    `
    document.head.appendChild(printStyle)
    window.print()
    setTimeout(() => {
      const el = document.getElementById('strategy-print-styles')
      if (el) el.remove()
    }, 1000)
  }

  return (
    <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700 space-y-8">
      <div className="flex items-center justify-between mb-4 no-print">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-[10px] font-black text-text-secondary/40 hover:text-orange-500 transition-all uppercase tracking-[0.2em]"
        >
          <ArrowLeftIcon className="w-4 h-4" /> BACK TO DASHBOARD
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={copyToClipboard}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/10 bg-white/5 text-[10px] font-black uppercase tracking-widest text-text-secondary hover:text-white hover:bg-white/10 transition-all shadow-inner"
            title="Copy summary to clipboard"
          >
            <DocumentDuplicateIcon className="w-4 h-4" /> COPY SUMMARY
          </button>
          <button
            onClick={exportAsPdf}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-none bg-gradient-to-r from-orange-500 to-orange-600 text-[10px] font-black uppercase tracking-[0.2em] text-white hover:scale-[1.02] active:scale-[0.98] transition-all shadow-[0_0_20px_rgba(255,107,0,0.3)]"
            id="ai-strategy-export-pdf"
          >
            <PrinterIcon className="w-4 h-4" /> EXPORT PDF
          </button>
        </div>
      </div>

      <div id="strategy-report-printable" ref={reportRef} className="space-y-8">
        <div className="bg-white border border-gray-200 rounded-3xl p-10 shadow-xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-80 h-80 bg-orange-50 rounded-full blur-[100px] -z-10 transition-all group-hover:bg-orange-100"></div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-orange-50 rounded-xl border border-orange-100">
                <SparklesIcon className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-[10px] font-black text-orange-600 uppercase tracking-[0.3em]">AI Strategy Report</span>
            </div>
            <h2 className="text-4xl font-black mb-6 uppercase tracking-tight font-heading leading-tight text-gray-900">
              Business Strategy & Automation Blueprint
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl font-medium italic border-l-4 border-orange-400 pl-8">{report.business_summary}</p>
          </div>
        </div>

        {sections.map(({ key, data, icon }) => (
          <div key={key} className="strategy-section bg-gray-50 rounded-3xl border border-gray-200 shadow-md p-8">
            <div className="flex items-center gap-4 mb-8">
              <div className="text-3xl p-3 bg-white rounded-2xl border border-gray-200 shadow-sm">{icon}</div>
              <h3 className="text-xl font-black text-gray-900 uppercase tracking-tight font-heading">
                {data.title}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {data.use_cases.map((uc, i) => (
                <UseCaseCard key={i} useCase={uc} index={i} />
              ))}
            </div>
          </div>
        ))}

        <div className="strategy-section bg-white rounded-3xl border border-gray-200 shadow-lg p-8">
          <div className="flex items-center gap-4 mb-10">
            <div className="p-3 bg-orange-100 rounded-2xl border border-orange-200">
              <ClockIcon className="w-7 h-7 text-orange-600" />
            </div>
            <h3 className="text-xl font-black text-gray-900 uppercase tracking-tight font-heading">Priority Roadmap</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { label: 'Phase 1: Quick Wins (Week 1-2)', items: report.priority_roadmap.week_1_2, color: 'orange', textClass: 'text-orange-600' },
              { label: 'Phase 2: Growth (Week 3-4)', items: report.priority_roadmap.week_3_4, color: 'blue', textClass: 'text-blue-600' },
              { label: 'Phase 3: Scale (Month 2+)', items: report.priority_roadmap.month_2_plus, color: 'purple', textClass: 'text-purple-600' },
            ].map(({ label, items, color, textClass }) => (
              <div key={label} className={`rounded-2xl border border-gray-200 p-6 bg-gray-50 shadow-sm group/phase transition-all`}>
                <h4 className={`text-[10px] font-black uppercase tracking-[0.2em] mb-6 ${textClass} text-opacity-80 group-hover:text-opacity-100`}>
                  {label}
                </h4>
                <ul className="space-y-4">
                  {items.map((item, i) => (
                    <li key={i} className="flex items-start gap-4 text-xs font-medium text-gray-600 leading-relaxed">
                      <CheckCircleIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${textClass} opacity-60 group-hover:opacity-100 transition-opacity`} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="strategy-section bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-10 border border-orange-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-2 bg-white rounded-xl shadow-sm border border-orange-100">
              <DocumentTextIcon className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-lg font-black uppercase tracking-[0.3em] text-orange-800">CTO Strategic Note</h3>
          </div>
          <p className="text-lg leading-relaxed font-medium italic border-l-4 border-orange-300 pl-8 text-gray-700">{report.cto_note}</p>
        </div>
      </div>
    </div>
  )
}

// ── Main Page Component ──────────────────────────────────────────────────────
export const AiStrategyPage: React.FC = () => {
  const { token } = useSelector((state: RootState) => state.auth)
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0)
  const [isLoading, setIsLoading] = useState(false)
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(true)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [initialMessages, setInitialMessages] = useState<ChatMessage[]>([])
  const [initialConfidence, setInitialConfidence] = useState<number>(30)
  const [report, setReport] = useState<Report | null>(null)

  // Fetch sessions on mount
  useEffect(() => {
    fetchSessions()
  }, [])

  const fetchSessions = async () => {
    setSessionsLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/ai-strategy/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = await res.json()
      if (json.success) {
        setSessions(json.data || [])
      }
    } catch (err) {
      console.error('[AiStrategy] Failed to fetch sessions:', err)
    } finally {
      setSessionsLoading(false)
    }
  }

  const handleResumeSession = async (sid: string) => {
    setIsLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/ai-strategy/session/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.message || 'Failed to load session')

      const data = json.data
      setSessionId(data.id)

      if (data.status === 'completed' && data.report) {
        // Jump to report
        setReport(data.report)
        setStep(3)
      } else {
        // Resume chat
        const msgs: ChatMessage[] = (data.messages || []).map((m: any) => ({
          role: m.role,
          content: m.content,
        }))
        setInitialMessages(msgs.length > 0 ? msgs : [{ role: 'assistant', content: 'Let\'s continue our conversation. What else can you tell me about your business?' }])
        setInitialConfidence(data.confidenceScore || 30)
        setStep(2)
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to resume session')
    } finally {
      setIsLoading(false)
    }
  }

  const handleFormSubmit = async (formData: FormData) => {
    setIsLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/ai-strategy/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      })

      const json = await res.json()

      if (!json.success) throw new Error(json.message || 'Failed to start session')

      setSessionId(json.data.sessionId)
      const aiMsg = json.data.aiMessage ||
        "Great, I've analyzed your business profile. Let me ask a few targeted questions to craft the best AI strategy for you.\n\n1. How does your sales team currently generate and qualify leads?\n2. What's your biggest operational bottleneck right now?"
      setInitialMessages([{ role: 'assistant', content: aiMsg }])
      setInitialConfidence(json.data.confidenceScore || 30)
      setStep(2)
      toast.success('Business profile captured! Let\'s dive deeper.')
    } catch (err: any) {
      toast.error(err.message || 'Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  const handleChatComplete = (reportData: Report) => {
    setReport(reportData)
    setStep(3)
    toast.success('Your AI Strategy Report is ready!')
  }

  const handleBackToDashboard = () => {
    setStep(0)
    setSessionId(null)
    setReport(null)
    setInitialMessages([])
    fetchSessions() // refresh the list
  }

  return (
    <div className="min-h-[80vh] p-6 lg:p-8 animate-in fade-in duration-500">
      {/* Page Header */}
      <div className="text-center mb-10">
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="p-2 bg-primary/10 rounded-xl border border-primary/20">
            <LightBulbIcon className="w-8 h-8 text-primary shadow-[0_0_15px_rgba(255,107,0,0.3)]" />
          </div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight font-heading">
            AI Business Architect
          </h1>
        </div>
        <p className="text-sm text-text-secondary font-medium max-w-lg mx-auto">
          Get a customized AI and automation strategy for your business.
        </p>
      </div>

      {/* Step Indicator */}
      <StepIndicator current={step} />

      {/* Step Content */}
      {step === 0 && (
        <ProjectDashboard
          sessions={sessions}
          isLoading={sessionsLoading}
          onNewProject={() => setStep(1)}
          onResumeSession={handleResumeSession}
        />
      )}
      {step === 1 && <StrategyForm onSubmit={handleFormSubmit} onBack={handleBackToDashboard} isLoading={isLoading} />}
      {step === 2 && sessionId && (
        <StrategyChat
          sessionId={sessionId}
          initialMessages={initialMessages}
          initialConfidence={initialConfidence}
          onComplete={handleChatComplete}
          onBack={handleBackToDashboard}
        />
      )}
      {step === 3 && report && <StrategyReport report={report} onBack={handleBackToDashboard} />}
    </div>
  )
}
